import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import driveApi, { errorMessage } from '../api/drive';
import { useDrive } from '../contexts/DriveProvider';
import { formatDate } from '../lib/files';
import { FileTag } from '../components/drive/ItemsView';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';

/** Every active guest link you created or manage, so nothing stays shared by accident. */
const LinksPage = () => {
  const { version, toast, bump } = useDrive();
  const [links, setLinks] = useState(null);

  useEffect(() => {
    driveApi.myLinks().then(setLinks).catch(() => setLinks([]));
  }, [version]);

  const revoke = async (link) => {
    try {
      await driveApi.revokeLink(link.id);
      toast(`Guest link to “${link.node.name}” turned off`);
      bump();
    } catch (err) {
      toast(errorMessage(err, 'Could not turn off the link.'), 'error');
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader><PageTitle>External links</PageTitle></PageHeader>
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-6">
      <p className="text-[13.5px] text-muted-foreground">Everything currently shared outside LampTwist that you created or manage. Change guests from the item’s Share button.</p>
      {links === null && <div className="h-40 animate-pulse rounded-2xl bg-muted" />}
      {links?.length === 0 && (
        <p className="mx-auto mt-12 text-center text-[13.5px] text-muted-foreground">Nothing is shared outside LampTwist right now.</p>
      )}
      <ul className="flex flex-col divide-y divide-border border-y border-border">
        {links?.map((link) => (
          <li key={link.id} className="flex flex-wrap items-center gap-4 py-3">
            <FileTag item={link.node} />
            <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-0.5">
              <Link
                to={link.node.kind === 'folder' ? `/drive/${link.node.drive}/folder/${link.node.id}` : `/drive/${link.node.drive}`}
                className="truncate font-semibold hover:underline"
              >
                {link.node.name}
              </Link>
              <span className="text-xs text-muted-foreground">
                In {link.node.drive_name}, shared by {link.created_by?.name}. {link.expires_at ? `Open until ${formatDate(link.expires_at)}` : 'No expiry'}
                {!link.allow_download && ', view only'}.
              </span>
            </div>
            <div className="flex flex-[1_1_220px] flex-wrap gap-1.5">
              {link.mode === 'anyone' ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-external-soft px-2.5 py-1 text-xs font-semibold text-external">
                  <Globe className="h-3 w-3" aria-hidden="true" /> Anyone with the link
                </span>
              ) : link.invites.map((inv) => (
                <span key={inv.id} className="rounded-full bg-external-soft px-2.5 py-1 text-xs font-semibold text-external" title={inv.last_opened_at ? `Opened ${formatDate(inv.last_opened_at)}` : 'Not opened yet'}>
                  {inv.email}
                </span>
              ))}
            </div>
            <button type="button" onClick={() => revoke(link)} className="h-8 rounded-lg px-2.5 text-[13px] font-medium text-destructive hover:bg-destructive/10">
              Turn off
            </button>
          </li>
        ))}
      </ul>
      </div>
    </div>
  );
};

export default LinksPage;
